import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FaChartBar, FaUsers, FaBox, FaClipboardList, FaMoneyBillWave,
  FaHandHoldingUsd, FaStar, FaChartLine, FaCog, FaSignOutAlt,
  FaBell, FaExclamationTriangle, FaLeaf, FaChevronDown,
  FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import NotificationBell from './NotificationBell';
import api from '../utils/api';
import { disconnectSocket } from '../utils/socket';
import './AdminLayout.css';

const menuItems = [
  { path: '/admin/dashboard', icon: FaChartBar, label: 'Dashboard', exact: true },
  {
    path: '/admin/users', icon: FaUsers, label: 'Users',
    submenu: [
      { path: '/admin/users/farmers', label: 'Farmers' },
      { path: '/admin/users/consumers', label: 'Consumers' }
    ]
  },
  { path: '/admin/products', icon: FaBox, label: 'Products' },
  { path: '/admin/orders', icon: FaClipboardList, label: 'Orders' },
  { path: '/admin/payments', icon: FaMoneyBillWave, label: 'Payments' },
  { path: '/admin/withdrawals', icon: FaHandHoldingUsd, label: 'Withdrawals' },
  { path: '/admin/reviews', icon: FaStar, label: 'Reviews' },
  { path: '/admin/order-reports', icon: FaExclamationTriangle, label: 'Reports' },
  { path: '/admin/reports', icon: FaChartLine, label: 'Analytics' },
  { path: '/admin/settings', icon: FaCog, label: 'Settings' }
];

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await api.put('/users/logout');
    } catch {
      /* still clear local session */
    }
    disconnectSocket();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div className="al">
      {/* Top Nav */}
      <header className="al-nav">
        <div className="al-nav-left">
          <button className="al-hamburger" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
            <span /><span /><span />
          </button>
          <Link to="/admin/dashboard" className="al-logo">
            <FaLeaf className="al-logo-icon" />
            <span className="al-logo-text">Taja Bazzar</span>
            <span className="al-logo-badge">Admin</span>
          </Link>
        </div>

        <div className="al-nav-right">
          <NotificationBell />
          <div className="al-profile" ref={dropdownRef}>
            <button className="al-profile-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
              <div className="al-avatar">{user.name?.charAt(0).toUpperCase() || 'A'}</div>
              <div className="al-profile-info">
                <span className="al-profile-name">{user.name || 'Admin'}</span>
                <span className="al-profile-role">Administrator</span>
              </div>
              <FaChevronDown className={`al-profile-arrow ${dropdownOpen ? 'al-profile-arrow--open' : ''}`} />
            </button>
            {dropdownOpen && (
              <div className="al-dropdown">
                <Link to="/notifications" className="al-dropdown-item" onClick={() => setDropdownOpen(false)}>
                  <FaBell /> Notifications
                </Link>
                <Link to="/admin/settings" className="al-dropdown-item" onClick={() => setDropdownOpen(false)}>
                  <FaCog /> Settings
                </Link>
                <div className="al-dropdown-sep" />
                <button className="al-dropdown-item al-dropdown-item--danger" onClick={handleLogout}>
                  <FaSignOutAlt /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="al-body">
        {/* Overlay for mobile */}
        {mobileOpen && <div className="al-overlay" onClick={() => setMobileOpen(false)} />}

        {/* Sidebar */}
        <aside className={`al-sidebar ${collapsed ? 'al-sidebar--collapsed' : ''} ${mobileOpen ? 'al-sidebar--open' : ''}`}>
          <button className="al-collapse-btn" onClick={() => setCollapsed(!collapsed)} title={collapsed ? 'Expand' : 'Collapse'}>
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>

          <nav className="al-menu">
            {menuItems.map((item, i) => (
              <div key={i} className="al-menu-group">
                <Link
                  to={item.path}
                  className={`al-menu-item ${isActive(item.path, item.exact) ? 'al-menu-item--active' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon className="al-menu-icon" />
                  {!collapsed && <span className="al-menu-label">{item.label}</span>}
                  {!collapsed && isActive(item.path, item.exact) && <span className="al-menu-dot" />}
                </Link>
                {item.submenu && !collapsed && (
                  <div className="al-submenu">
                    {item.submenu.map((sub, j) => (
                      <Link
                        key={j}
                        to={sub.path}
                        className={`al-submenu-item ${isActive(sub.path) ? 'al-submenu-item--active' : ''}`}
                      >
                        <span className="al-submenu-dot" />
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="al-sidebar-footer">
            <button className="al-logout-btn" onClick={handleLogout} title={collapsed ? 'Logout' : undefined}>
              <FaSignOutAlt />
              {!collapsed && <span>Logout</span>}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="al-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
